/**
 * Tenant settings admin page (v4.1 Phase 37.7).
 *
 * Gated by the existing admin-panel guard (any admin role). The API
 * backend enforces tenant-admin OR super-admin; members hitting this
 * route by URL-guessing get 403 from the API.
 *
 * v4.1 fields:
 *   - shareLinkDefaultExpiryDays
 *   - shareLinkMaxExpiryDays
 *
 * Future per-tenant toggles land here; no URL changes required.
 */

import { useEffect, useState } from "react";
import {
  getTenantSettings,
  updateTenantSettings,
} from "../services/tenantSettings.js";
import { useStore } from "../store/useStore.js";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminTenantSettings() {
  const pushToast = useStore((s) => s.pushToast);
  const activeTenantId = useStore((s) => s.activeTenantId);

  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTenantSettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        setDraft({
          shareLinkDefaultExpiryDays: s.shareLinkDefaultExpiryDays,
          shareLinkMaxExpiryDays: s.shareLinkMaxExpiryDays,
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // Refetch when the admin's active tenant changes (super-admin switcher).
  }, [activeTenantId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const updated = await updateTenantSettings({
        shareLinkDefaultExpiryDays: Number(draft.shareLinkDefaultExpiryDays),
        shareLinkMaxExpiryDays: Number(draft.shareLinkMaxExpiryDays),
      });
      setSettings(updated);
      pushToast("Tenant settings saved", "success");
    } catch (err) {
      setError(err.message);
      pushToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <LoadingSkeleton variant="line" />
        <LoadingSkeleton variant="line" />
        <LoadingSkeleton variant="line" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="admin-container">
        <p className="admin-error">
          Failed to load tenant settings: {error || "unknown error"}
        </p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>Tenant settings</h1>
        <p className="admin-subtitle">
          Per-tenant policy knobs for <strong>{settings.tenantId}</strong>.
          Super-admins can switch tenants via the header switcher.
        </p>
      </header>

      <form className="admin-form" onSubmit={handleSave}>
        <fieldset className="admin-fieldset">
          <legend>Share-link expiry</legend>
          <label className="admin-field">
            <span>Default expiry (days)</span>
            <input
              type="number"
              min="1"
              max={draft.shareLinkMaxExpiryDays || 90}
              value={draft.shareLinkDefaultExpiryDays ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  shareLinkDefaultExpiryDays: e.target.value,
                }))
              }
              required
            />
            <span className="admin-field-hint">
              Applied when a share creator doesn&apos;t specify an expiry.
            </span>
          </label>
          <label className="admin-field">
            <span>Maximum expiry (days)</span>
            <input
              type="number"
              min="1"
              max="90"
              value={draft.shareLinkMaxExpiryDays ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  shareLinkMaxExpiryDays: e.target.value,
                }))
              }
              required
            />
            <span className="admin-field-hint">
              Upper bound creators cannot exceed. Cap of 90.
            </span>
          </label>
        </fieldset>
        {error && <p className="admin-error">{error}</p>}
        <div className="admin-form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      {settings.updatedAt && (
        <p className="admin-audit">
          Last updated {new Date(settings.updatedAt).toLocaleString()} by{" "}
          {settings.updatedBy}
        </p>
      )}
    </div>
  );
}
